import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { handleCopyText } from '@/utils/platform';

import { useSectionExpansion } from '../chat-debug.expansion';
import { chatDebugStore } from '../chat-debug.store';
import {
  ZERO_COST_BREAKDOWN,
  sumBilledTokens,
  sumCostBreakdowns,
  type CostBreakdown,
  type CompactionCallCost,
  type SubagentRunCost,
  type TurnCostSummary,
  type PayloadSection,
  type DebugPayloadSkeleton,
} from '../chat-debug.types';

export function formatTokens(count: number | undefined | null): string {
  if (!count) return '0';
  return count.toLocaleString();
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatUsd(amount: number | undefined | null): string {
  if (amount === null || amount === undefined) return '$—';
  if (amount === 0) return '$0.000000';
  return '$' + amount.toFixed(6);
}

export interface CostSummaryTableProps {
  breakdown: CostBreakdown;
  label?: string;
}

export function CostSummaryTable({ breakdown, label }: CostSummaryTableProps) {
  const rows: [string, number, number][] = [
    [
      'Fresh input',
      breakdown.fresh_input_tokens,
      breakdown.fresh_input_cost_usd,
    ],
    ['Output', breakdown.output_tokens, breakdown.output_cost_usd],
    ['Thoughts', breakdown.thought_tokens, breakdown.thought_cost_usd],
    [
      'Cache read',
      breakdown.cached_read_tokens,
      breakdown.cached_read_cost_usd,
    ],
    [
      'Cache write',
      breakdown.cache_write_tokens,
      breakdown.cache_write_cost_usd,
    ],
  ].filter(([, tokens]) => (tokens as number) > 0) as [
    string,
    number,
    number,
  ][];

  const totalBilledTokens = sumBilledTokens(breakdown);

  return (
    <YStack className="gap-1">
      {label && (
        <span className="text-xs font-medium text-foreground-subtle uppercase tracking-wide">
          {label}
        </span>
      )}
      <div className="bg-surface/50 rounded border border-border p-2.5">
        <table className="w-full text-xs">
          <tbody>
            {rows.map(([rowLabel, tokens, cost]) => (
              <tr key={rowLabel}>
                <td className="text-foreground-subtle py-0.5 pr-3">
                  {rowLabel}
                </td>
                <td className="text-right font-mono text-foreground-muted tabular-nums">
                  {formatTokens(tokens)} tok
                </td>
                <td className="text-right font-mono text-foreground pl-4 tabular-nums">
                  {formatUsd(cost)}
                </td>
              </tr>
            ))}
            <tr className="border-t border-border font-semibold">
              <td className="text-foreground pt-1.5">Total</td>
              <td className="text-right font-mono text-foreground-muted pt-1.5 tabular-nums">
                {formatTokens(totalBilledTokens)} tok
              </td>
              <td className="text-right font-mono text-blue-500 pt-1.5 pl-4 tabular-nums">
                {formatUsd(breakdown.total_usd)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </YStack>
  );
}

function findCacheBreakIndex(
  sections: PayloadSection[],
  previousSections: PayloadSection[]
): number {
  for (let i = 0; i < Math.min(sections.length, previousSections.length); i++) {
    const current = sections[i];
    const previous = previousSections[i];
    const matches =
      current.content_hash && previous.content_hash
        ? current.content_hash === previous.content_hash
        : current.character_count === previous.character_count;
    if (!matches) return i;
  }
  return -1;
}

const SECTION_ICONS: Record<string, string> = {
  system_prompt_static: '⚙️',
  system_prompt_dynamic: '⚙️',
  tool_definition: '🔧',
  user_message: '👤',
  assistant_message: '🤖',
  thoughts: '💭',
  tool_call: '📤',
  tool_result: '📥',
  file: '📎',
  image: '🖼️',
  unknown: '·',
};

interface PayloadSectionsTableProps {
  sections: PayloadSection[];
  previousSections: PayloadSection[];
  cacheMissReason?: string | null;
}

function PayloadSectionsTable({
  sections,
  previousSections,
  cacheMissReason,
}: PayloadSectionsTableProps) {
  if (!sections.length) {
    return <span className="text-xs text-foreground-subtle">No sections.</span>;
  }

  const firstBreakIndex = findCacheBreakIndex(sections, previousSections);

  return (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="text-foreground-subtle uppercase tracking-wider border-b border-border">
          <th className="text-right pr-2 py-1 font-medium w-6">#</th>
          <th className="text-left pr-2 py-1 font-medium">Section</th>
          <th className="text-right px-2 py-1 font-medium w-16">Tokens</th>
          <th className="text-right px-2 py-1 font-medium w-20">Delta</th>
        </tr>
      </thead>
      <tbody>
        {sections.map((section, index) => {
          const isNew = index >= previousSections.length;
          const isFirstBreak = index === firstBreakIndex;
          const isFresh =
            isNew || (firstBreakIndex >= 0 && index >= firstBreakIndex);

          return (
            <React.Fragment key={index}>
              {isFirstBreak && (
                <tr>
                  <td colSpan={4} className="py-px">
                    <div className="flex items-center gap-1">
                      <div className="h-px flex-1 bg-red-400/40" />
                      <span className="text-[8px] text-red-400 whitespace-nowrap leading-none">
                        cache invalidated
                        {cacheMissReason ? ` · ${cacheMissReason}` : ''} ↓
                      </span>
                      <div className="h-px flex-1 bg-red-400/40" />
                    </div>
                  </td>
                </tr>
              )}
              <tr
                className={cn(
                  isFresh
                    ? 'bg-emerald-500/5'
                    : index % 2 === 1
                      ? 'bg-surface/30'
                      : ''
                )}
              >
                <td className="text-right text-foreground-subtle pr-2 py-0.5 font-mono tabular-nums">
                  {section.index + 1}
                </td>
                <td className="pr-2 py-0.5 text-foreground-muted max-w-0 truncate">
                  <span>
                    {SECTION_ICONS[section.section_type] ?? '·'} {section.label}
                  </span>
                </td>
                <td className="text-right font-mono text-foreground-muted px-2 py-0.5 tabular-nums">
                  {formatTokens(section.token_count)}
                </td>
                <td className="text-right px-2 py-0.5 whitespace-nowrap">
                  {isFresh && (
                    <span className="text-[9px] font-mono text-emerald-500 tabular-nums">
                      {formatTokens(section.token_count)}
                    </span>
                  )}
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

interface TokenUsageLineProps {
  cost: CostBreakdown;
  className?: string;
  showTotal?: boolean;
  totalClassName?: string;
}

function TokenUsageLine({
  cost,
  className,
  showTotal = true,
  totalClassName = 'font-semibold',
}: TokenUsageLineProps) {
  return (
    <XStack
      className={cn(
        'gap-3 font-mono text-foreground-subtle tabular-nums flex-wrap',
        className
      )}
    >
      <span className="text-amber-500">
        W {formatTokens(cost.cache_write_tokens)}
      </span>
      <span className="text-purple-500">
        R {formatTokens(cost.cached_read_tokens)}
      </span>
      <span className="text-blue-500">
        I {formatTokens(cost.fresh_input_tokens)}
      </span>
      <span className="text-green-500">
        O{' '}
        {formatTokens(
          cost.output_tokens +
            (cost.thought_cost_usd > 0 ? cost.thought_tokens : 0)
        )}
      </span>
      {showTotal && (
        <span className={cn('text-foreground', totalClassName)}>
          {formatUsd(cost.total_usd)}
        </span>
      )}
    </XStack>
  );
}

interface StepRowProps {
  chatId: string;
  sectionKey: string;
  stepIndex: number;
  stepCost: CostBreakdown | undefined;
  hasCacheBreak: boolean;
  cacheMissReason: string | null;
  payload: DebugPayloadSkeleton | undefined;
  payloadSections: PayloadSection[];
  previousPayloadSections: PayloadSection[];
}

const StepRow = observer(function StepRow({
  chatId,
  sectionKey,
  stepIndex,
  stepCost,
  hasCacheBreak,
  cacheMissReason,
  payload,
  payloadSections,
  previousPayloadSections,
}: StepRowProps) {
  const { isExpanded, toggle } = useSectionExpansion(sectionKey);

  const handleCopyPayload = async () => {
    if (!payload) return;
    try {
      // The backend reconstructs the original {model, system_prompt, tools,
      // message_chain} from the stored sections - single source of truth shared
      // with the HTML debugger.
      const fullPayload = await backendApis.debug.getDebugFullPayload(
        chatId,
        payload.message_id,
        payload.step_index ?? stepIndex
      );
      await handleCopyText(JSON.stringify(fullPayload, null, 2), false);
      toast.info('Payload copied to clipboard');
    } catch {
      toast.error('Failed to copy payload');
    }
  };

  return (
    <YStack className="border-t border-border first:border-t-0">
      <XStack
        className="items-start gap-2 py-2 px-1 cursor-pointer hover:bg-surface/50 transition-colors rounded-sm"
        onClick={toggle}
      >
        <Icons.chevronRight
          className={cn(
            'size-3 shrink-0 mt-0.5 text-foreground-subtle transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <YStack className="flex-1 min-w-0 gap-0.5">
          <XStack className="items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium text-foreground">
              Step {stepIndex + 1}
            </span>
            {hasCacheBreak && (
              <span className="text-[9px] text-red-400">
                cache break{cacheMissReason ? ` · ${cacheMissReason}` : ''}
              </span>
            )}
          </XStack>
          {stepCost && (
            <TokenUsageLine cost={stepCost} className="text-[10px]" />
          )}
        </YStack>
        {payload && (
          <button
            className="shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-border bg-surface hover:bg-surface-hover text-foreground-subtle transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyPayload();
            }}
          >
            Copy payload
          </button>
        )}
      </XStack>

      {isExpanded && payloadSections.length > 0 && (
        <div className="pl-5 pr-1 pb-3">
          <PayloadSectionsTable
            sections={payloadSections}
            previousSections={previousPayloadSections}
            cacheMissReason={cacheMissReason}
          />
        </div>
      )}
    </YStack>
  );
});

interface TurnRowProps {
  chatId: string;
  turn: TurnCostSummary;
  turnIndex: number;
  payloads: DebugPayloadSkeleton[];
  previousTurnLastSections: PayloadSection[];
  subagentPayloadsByChatId: Record<string, DebugPayloadSkeleton[]>;
}

const TurnRow = observer(function TurnRow({
  chatId,
  turn,
  turnIndex,
  payloads,
  previousTurnLastSections,
  subagentPayloadsByChatId,
}: TurnRowProps) {
  const { isExpanded, toggle } = useSectionExpansion(`turn:${turn.message_id}`);

  // Join payloads to steps by step_index (positional fallback for legacy rows
  // translated without one)
  const payloadByStep = new Map<number, DebugPayloadSkeleton>();
  payloads.forEach((payload, index) => {
    payloadByStep.set(payload.step_index ?? index, payload);
  });
  const stepsWithSections = turn.steps.map((_, stepIndex) => {
    return payloadByStep.get(stepIndex)?.sections ?? ([] as PayloadSection[]);
  });

  const cacheBreakStepIndices: number[] = [];
  let previousSections = previousTurnLastSections;
  stepsWithSections.forEach((sections, stepIndex) => {
    if (sections.length > 0) {
      const breakIndex = findCacheBreakIndex(sections, previousSections);
      if (breakIndex >= 0) cacheBreakStepIndices.push(stepIndex);
      previousSections = sections;
    }
  });

  return (
    <YStack className="border-t border-border first:border-t-0">
      <XStack
        className="items-start gap-2 py-2.5 px-1 cursor-pointer hover:bg-surface/50 transition-colors rounded-sm"
        onClick={toggle}
      >
        <Icons.chevronRight
          className={cn(
            'size-3.5 shrink-0 mt-0.5 text-foreground-subtle transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <YStack className="flex-1 min-w-0 gap-0">
          <XStack className="items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-foreground">
              Turn {turnIndex + 1}
            </span>
            <span className="text-xs font-mono text-foreground-muted">
              {formatTimestamp(turn.created_at)}
              {turn.cache_expired === true && (
                <span className="text-[9px] text-red-400 ml-1.5">
                  · cache expired ({Math.floor((turn.turn_gap_ms ?? 0) / 60000)}
                  m gap)
                </span>
              )}
            </span>
          </XStack>
          <XStack className="items-center gap-1.5">
            <span className="text-xs text-foreground-subtle">
              {turn.steps.length} step{turn.steps.length !== 1 ? 's' : ''}
              {turn.subagent_runs.length > 0 && (
                <>
                  {' · '}
                  <span className="text-amber-500">
                    {turn.subagent_runs.length} subagent run
                    {turn.subagent_runs.length !== 1 ? 's' : ''}
                  </span>
                </>
              )}
            </span>
            {cacheBreakStepIndices.length > 0 && (
              <span className="text-[9px] text-red-400">
                cache break in step{' '}
                {cacheBreakStepIndices.map((i) => i + 1).join(', ')}
              </span>
            )}
          </XStack>
          <TokenUsageLine
            cost={turn.total_cost}
            className="text-xs"
            totalClassName="font-bold"
          />
        </YStack>
      </XStack>

      {isExpanded && (
        <YStack className="pl-5 pr-1 pb-2 gap-2">
          {turn.steps.map((step, stepIndex) => {
            const stepCost = turn.step_costs[stepIndex];
            const payload = payloadByStep.get(stepIndex);
            const sections = stepsWithSections[stepIndex] ?? [];
            const prevSections =
              stepIndex === 0
                ? previousTurnLastSections
                : (stepsWithSections[stepIndex - 1] ?? []);
            const hasCacheBreak = cacheBreakStepIndices.includes(stepIndex);

            return (
              <StepRow
                key={`${step.model}-${stepIndex}`}
                chatId={chatId}
                sectionKey={`step:${turn.message_id}:${stepIndex}`}
                stepIndex={stepIndex}
                stepCost={stepCost}
                hasCacheBreak={hasCacheBreak}
                cacheMissReason={payload?.cache_miss_reason ?? null}
                payload={payload}
                payloadSections={sections}
                previousPayloadSections={prevSections}
              />
            );
          })}
          {turn.subagent_runs.length > 0 && (
            <SubagentRunsList
              sectionKey={`subagent-runs:${turn.message_id}`}
              runs={turn.subagent_runs}
              total={turn.subagent_total}
              subagentPayloadsByChatId={subagentPayloadsByChatId}
            />
          )}
        </YStack>
      )}
    </YStack>
  );
});

interface SubagentRunsListProps {
  sectionKey: string;
  runs: SubagentRunCost[];
  total: CostBreakdown | null;
  subagentPayloadsByChatId: Record<string, DebugPayloadSkeleton[]>;
}

const SubagentRunsList = observer(function SubagentRunsList({
  sectionKey,
  runs,
  total,
  subagentPayloadsByChatId,
}: SubagentRunsListProps) {
  const { isExpanded, toggle } = useSectionExpansion(sectionKey, true);
  if (!runs.length) return null;

  return (
    <YStack className="gap-1">
      <XStack className="items-center gap-1 cursor-pointer" onClick={toggle}>
        <Icons.chevronRight
          className={cn(
            'size-3 text-amber-500 transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <span className="text-xs font-medium text-amber-500 uppercase tracking-wide">
          Subagent Runs ({runs.length})
        </span>
        {total && (
          <span className="text-xs font-mono text-amber-500 tabular-nums ml-auto">
            {formatUsd(total.total_usd)}
          </span>
        )}
      </XStack>
      {isExpanded && (
        <YStack className="rounded border border-amber-500/30 overflow-hidden">
          {runs.map((run) => (
            <SubagentRunRow
              key={run.child_chat_id}
              run={run}
              payloads={subagentPayloadsByChatId[run.child_chat_id] ?? []}
            />
          ))}
        </YStack>
      )}
    </YStack>
  );
});

interface SubagentRunRowProps {
  run: SubagentRunCost;
  payloads: DebugPayloadSkeleton[];
}

const SubagentRunRow = observer(function SubagentRunRow({
  run,
  payloads,
}: SubagentRunRowProps) {
  const { isExpanded, toggle } = useSectionExpansion(
    `subagent-run:${run.child_chat_id}`
  );
  const stepCount = run.steps.length;
  const compactionCount = run.compaction_calls.length;

  // Subagent payloads span the child chat's messages; join on
  // (message_id, step_index) with positional fallback for legacy rows
  const payloadByStep = new Map<string, DebugPayloadSkeleton>();
  payloads.forEach((payload) => {
    if (payload.step_index != null) {
      payloadByStep.set(`${payload.message_id}:${payload.step_index}`, payload);
    }
  });
  const stepPayloads = run.steps.map(
    (step, stepIndex) =>
      payloadByStep.get(`${step.message_id}:${step.step_index}`) ??
      payloads[stepIndex]
  );
  const stepsWithSections = stepPayloads.map((payload) => {
    return payload?.sections ?? ([] as PayloadSection[]);
  });

  const cacheBreakStepIndices: number[] = [];
  let previousSections: PayloadSection[] = [];
  stepsWithSections.forEach((sections, stepIndex) => {
    if (sections.length > 0) {
      const breakIndex = findCacheBreakIndex(sections, previousSections);
      if (breakIndex >= 0 && stepIndex > 0) {
        cacheBreakStepIndices.push(stepIndex);
      }
      previousSections = sections;
    }
  });

  return (
    <YStack className="border-b border-amber-500/20 last:border-b-0 bg-amber-500/5">
      <XStack
        className="items-start gap-2 px-3 py-2 cursor-pointer hover:bg-amber-500/10 transition-colors"
        onClick={toggle}
      >
        <Icons.chevronRight
          className={cn(
            'size-3 shrink-0 mt-1 text-amber-500 transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <YStack className="flex-1 min-w-0 gap-0.5">
          <XStack className="items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-foreground truncate">
              {run.agent}
            </span>
            <span className="text-[10px] font-mono text-foreground-muted">
              {formatTimestamp(run.created_at)}
            </span>
          </XStack>
          <span className="text-[11px] text-foreground-subtle truncate">
            {run.description}
          </span>
          <span className="text-[10px] text-foreground-muted">
            {stepCount} step{stepCount !== 1 ? 's' : ''}
            {compactionCount > 0 && (
              <>
                {' · '}
                {compactionCount} compaction{compactionCount !== 1 ? 's' : ''}
              </>
            )}
            {payloads.length === 0 && stepCount > 0 && (
              <span className="text-[9px] text-red-400 ml-1.5">
                · payloads unavailable
              </span>
            )}
          </span>
        </YStack>
        <span className="text-xs font-mono font-semibold text-amber-500 tabular-nums shrink-0">
          {formatUsd(run.total_cost.total_usd)}
        </span>
      </XStack>
      {isExpanded && (
        <YStack className="px-3 pb-2 gap-1">
          {run.steps.map((step, stepIndex) => {
            const payload = stepPayloads[stepIndex];
            const sections = stepsWithSections[stepIndex] ?? [];
            const prevSections =
              stepIndex === 0 ? [] : (stepsWithSections[stepIndex - 1] ?? []);
            const hasCacheBreak = cacheBreakStepIndices.includes(stepIndex);
            return (
              <StepRow
                key={`${step.message_id}-${stepIndex}`}
                chatId={run.child_chat_id}
                sectionKey={`step:${run.child_chat_id}:${step.message_id}:${stepIndex}`}
                stepIndex={stepIndex}
                stepCost={step.cost}
                hasCacheBreak={hasCacheBreak}
                cacheMissReason={payload?.cache_miss_reason ?? null}
                payload={payload}
                payloadSections={sections}
                previousPayloadSections={prevSections}
              />
            );
          })}
          {run.compaction_calls.length > 0 && (
            <CompactionCallsList
              sectionKey={`compaction:${run.child_chat_id}`}
              calls={run.compaction_calls}
            />
          )}
        </YStack>
      )}
    </YStack>
  );
});

interface CompactionCallsListProps {
  sectionKey: string;
  calls: CompactionCallCost[];
  title?: string;
}

const CompactionCallsList = observer(function CompactionCallsList({
  sectionKey,
  calls,
  title = 'Compaction Calls',
}: CompactionCallsListProps) {
  const { isExpanded, toggle } = useSectionExpansion(sectionKey, true);

  if (!calls.length) return null;

  return (
    <YStack className="gap-1">
      <XStack className="items-center gap-1 cursor-pointer" onClick={toggle}>
        <Icons.chevronRight
          className={cn(
            'size-3 text-foreground-subtle transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <span className="text-xs font-medium text-foreground-subtle uppercase tracking-wide">
          {title} ({calls.length})
        </span>
      </XStack>
      {isExpanded && (
        <div className="rounded border border-border overflow-hidden">
          {calls.map((call, index) => (
            <XStack
              key={index}
              className="items-start justify-between gap-2 px-3 py-2 border-b border-border last:border-b-0 bg-surface/30"
            >
              <YStack className="gap-0.5">
                <span className="text-xs font-mono text-foreground-muted">
                  {call.model}
                </span>
                <TokenUsageLine
                  cost={call.cost}
                  className="gap-2 text-[10px]"
                  showTotal={false}
                />
              </YStack>
              <span className="text-xs font-mono font-semibold text-foreground tabular-nums shrink-0">
                {formatUsd(call.cost.total_usd)}
              </span>
            </XStack>
          ))}
        </div>
      )}
    </YStack>
  );
});

interface GrandTotalRowProps {
  grandTotalUsd: number | null | undefined;
}

function GrandTotalRow({ grandTotalUsd }: GrandTotalRowProps) {
  if (grandTotalUsd === null || grandTotalUsd === undefined) return null;
  return (
    <XStack className="items-center justify-between px-3 py-2.5 rounded border border-border bg-surface/50">
      <span className="text-sm font-semibold text-foreground">
        Grand Total (agent + compaction)
      </span>
      <span className="text-sm font-mono font-bold text-indigo-500 tabular-nums">
        {formatUsd(grandTotalUsd)}
      </span>
    </XStack>
  );
}

function TokenLegend() {
  return (
    <XStack className="gap-2 flex-wrap">
      {[
        { label: 'W = Cache write', color: 'text-amber-500' },
        { label: 'R = Cache read', color: 'text-purple-500' },
        { label: 'I = Fresh input', color: 'text-blue-500' },
        { label: 'O = Output', color: 'text-green-500' },
      ].map(({ label, color }) => (
        <span key={label} className={cn('text-[10px] font-mono', color)}>
          {label}
        </span>
      ))}
    </XStack>
  );
}

interface TurnsSectionProps {
  chatId: string;
  turns: TurnCostSummary[];
  payloadsByTurn: DebugPayloadSkeleton[][];
  subagentPayloadsByChatId: Record<string, DebugPayloadSkeleton[]>;
}

const TurnsSection = observer(function TurnsSection({
  chatId,
  turns,
  payloadsByTurn,
  subagentPayloadsByChatId,
}: TurnsSectionProps) {
  const { isExpanded, toggle } = useSectionExpansion('turns', true);

  const turnRows = useMemo(() => {
    let carryOverSections: PayloadSection[] = [];
    return turns.map((turn, index) => {
      const previousTurnLastSections = carryOverSections;
      const turnPayloads = payloadsByTurn[index] ?? [];
      const lastPayloadWithSections = [...turnPayloads]
        .reverse()
        .find((payload) => payload.sections.length > 0);
      carryOverSections =
        lastPayloadWithSections?.sections ?? carryOverSections;
      return {
        turn,
        index,
        turnPayloads,
        previousTurnLastSections,
      };
    });
  }, [turns, payloadsByTurn]);

  return (
    <YStack className="gap-1">
      <XStack className="items-center gap-1 cursor-pointer" onClick={toggle}>
        <Icons.chevronRight
          className={cn(
            'size-3 text-foreground-subtle transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <span className="text-xs font-medium text-foreground-subtle uppercase tracking-wide">
          Turns ({turns.length})
        </span>
      </XStack>
      {isExpanded && (
        <div className="rounded border border-border overflow-hidden">
          {turnRows.map(
            ({ turn, index, turnPayloads, previousTurnLastSections }) => (
              <TurnRow
                key={turn.message_id}
                chatId={chatId}
                turn={turn}
                turnIndex={index}
                payloads={turnPayloads}
                previousTurnLastSections={previousTurnLastSections}
                subagentPayloadsByChatId={subagentPayloadsByChatId}
              />
            )
          )}
        </div>
      )}
    </YStack>
  );
});

export const LlmCostsViewer = observer(() => {
  const session = chatDebugStore.active;
  const chatCostSummary = session?.chatCostSummary ?? null;
  const payloadSkeletons = session?.payloadSkeletons ?? null;
  const loadingCosts = session?.loadingCosts ?? false;

  const agentCallsTotal = useMemo(
    () =>
      (chatCostSummary?.turns ?? []).reduce(
        (acc, turn) => sumCostBreakdowns(acc, turn.total_cost),
        ZERO_COST_BREAKDOWN
      ),
    [chatCostSummary?.turns]
  );

  const payloadsByTurn = useMemo(() => {
    const turns = chatCostSummary?.turns ?? [];
    const allPayloads = payloadSkeletons?.payloads ?? [];
    const byMessageId = new Map<string, DebugPayloadSkeleton[]>();
    for (const payload of allPayloads) {
      const group = byMessageId.get(payload.message_id) ?? [];
      group.push(payload);
      byMessageId.set(payload.message_id, group);
    }
    return turns.map((turn) => byMessageId.get(turn.message_id) ?? []);
  }, [chatCostSummary?.turns, payloadSkeletons?.payloads]);

  const subagentPayloadsByChatId = useMemo(
    () => payloadSkeletons?.subagent_payloads ?? {},
    [payloadSkeletons?.subagent_payloads]
  );

  if (!session) return null;

  if (loadingCosts && !chatCostSummary) {
    return (
      <YStack className="py-6 items-center justify-center gap-2">
        <Icons.refresh className="size-4 animate-spin text-foreground-subtle" />
        <span className="text-xs text-foreground-subtle">
          Loading cost data...
        </span>
      </YStack>
    );
  }

  if (payloadSkeletons?.debug_data_scrubbed) {
    return (
      <YStack className="py-6 items-center justify-center gap-2">
        <span className="text-sm text-foreground-subtle text-center px-4">
          This chat was untouched for a week, which is why we deleted all LLM
          debug data from it.
        </span>
      </YStack>
    );
  }

  if (!chatCostSummary) {
    return (
      <YStack className="py-6 items-center justify-center gap-2">
        <span className="text-sm text-foreground-subtle">
          {session?.costsError ?? 'No cost data available'}
        </span>
        <button
          onClick={() => session.fetchDebugData()}
          className="text-sm text-foreground-subtle underline hover:text-foreground"
        >
          {session?.costsError ? 'Click to retry' : 'Click to load'}
        </button>
      </YStack>
    );
  }

  const {
    turns,
    compaction_calls: compactionCalls,
    compaction_total: compactionTotal,
    mid_turn_compaction_calls: midTurnCompactionCalls,
    mid_turn_compaction_total: midTurnCompactionTotal,
    grand_total_usd: grandTotalUsd,
  } = chatCostSummary;

  return (
    <YStack className="py-3 gap-4">
      <TokenLegend />

      {turns.length > 0 && (
        <CostSummaryTable
          breakdown={agentCallsTotal}
          label="Agent Calls Cost"
        />
      )}

      {compactionCalls.length > 0 && compactionTotal && (
        <CostSummaryTable
          breakdown={compactionTotal}
          label="Compaction Total"
        />
      )}

      {midTurnCompactionCalls.length > 0 && midTurnCompactionTotal && (
        <CostSummaryTable
          breakdown={midTurnCompactionTotal}
          label="Mid-turn Compaction Total"
        />
      )}

      <GrandTotalRow grandTotalUsd={grandTotalUsd} />

      {compactionCalls.length > 0 && (
        <CompactionCallsList
          sectionKey="compaction:main"
          calls={compactionCalls}
        />
      )}

      {midTurnCompactionCalls.length > 0 && (
        <CompactionCallsList
          sectionKey="compaction:mid-turn"
          calls={midTurnCompactionCalls}
          title="Mid-turn Compaction Calls"
        />
      )}

      {turns.length > 0 && (
        <TurnsSection
          chatId={session.chatId}
          turns={turns}
          payloadsByTurn={payloadsByTurn}
          subagentPayloadsByChatId={subagentPayloadsByChatId}
        />
      )}
    </YStack>
  );
});
